'use client';

import { jsx as _jsx } from "react/jsx-runtime";
/**
 * `SdkAuthProvider` — relocated from `frontend/src/providers/auth-provider.tsx`
 * into `@morfi/backend-sdk-v2/platform/web/auth/` as part of the auth-cutover
 * (spec 009 FILE 14 Group B).
 *
 * Owns: session-restore (`tryRestoreSession`: refresh-session → fetch profile),
 * logout, and the auth-store mirror. App-specific side-effects (toast,
 * analytics events, onboarding reset, NSFW store reset, telemetry identity
 * reset) are injected as callback props so the SDK core stays free of
 * frontend-specific dependencies.
 *
 * Lives in `platform/web/` because:
 *   - Reads `document.cookie` and `localStorage` (web-only).
 *   - Uses `fetch('/v1/me')` (universal, but coupled to the web SDK fetch-shim).
 */
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useEffectEvent, useRef, useState } from 'react';
import { useAuthSync } from '../../../../auth/oauth/auth-sync.js';
import { AuthActionsProvider } from '../../../../auth/state/auth-actions-context.js';
import { getSdkAuthStore } from '../../../../auth/state/auth-state.js';
import { clearUserStorage } from '../../../../auth/state/cleanup.js';
import { readAuthPrivateAccount, subscribeAuthPrivateAccount, writeAuthPrivateAccount } from '../../../../auth/state/private-account-auth.js';
import { useLogout } from '../../../../generated/mutations/useLogout.gen.js';
import { useRefreshSession } from '../../../../generated/mutations/useRefreshSession.gen.js';
import { useSdkNetworkScope, useSdkViewerScope } from '../../../../provider/SdkRuntimeContext.js';
import { classifySessionFailure } from '../../../shared/auth/session-diagnostics.js';
import { OauthConnectStash } from '../oauth/oauth-connect-stash.js';
import { recordSessionExpiry, startRefreshScheduler } from './refresh-scheduler.js';
// ── Session hint (web-only) ───────────────────────────────────────────────────
/**
 * Check if there's any indication of an active session before making network
 * requests.
 *
 * The `morfi_session` cookie is `httpOnly` (backend `service.ts`), so
 * `document.cookie` can NEVER see it from JS — that check only ever fires
 * for a non-httpOnly variant. The durable, JS-readable hint is
 * `morfi_user_id` in `localStorage`: the session-restore + auth-success
 * flows write it (`localStorage.setItem('morfi_user_id', user.id)`) and
 * `clearUserStorage()` removes it on logout. A `morfi_bearer_token` is
 * also accepted for parity with RN / explicit-token flows.
 *
 * A false positive here is harmless — `fetchMeProfile()` cleanly returns
 * `null` on a 401/403 — but a false negative (the previous behaviour,
 * when only the httpOnly-cookie check existed) silently skips restore
 * and leaves a fully-authenticated user signed-out.
 */
function hasSessionHint() {
  try {
    /* v8 ignore next -- SSR guard: jsdom always defines `document`, so the undefined arm is unreachable in tests */
    if (typeof document !== 'undefined' && document.cookie.includes('morfi_session')) {
      return true;
    }
    if (typeof localStorage !== 'undefined') {
      if (localStorage.getItem('morfi_user_id') !== null) return true;
      if (localStorage.getItem('morfi_bearer_token') !== null) return true;
    }
  } catch {
    // SILENT-CATCH: SSR or restricted localStorage access — expected, fall through
  }
  return false;
}
/**
 * Probe `/v1/me` and classify the outcome.
 *
 * The `morfi_session` cookie is independently valid for its full lifetime —
 * `GET /v1/me` succeeds on a plain cookie with NO preceding `/v1/auth/refresh`.
 * So restore probes here FIRST and only falls back to a (session-rotating)
 * refresh when this returns `unauthorized`. See `tryRestoreSession`.
 */
async function probeMeProfile() {
  let res;
  try {
    res = await fetch('/v1/me', {
      credentials: 'include'
    });
  } catch {
    return {
      kind: 'error',
      reason: 'network'
    };
  }
  if (res.status === 401 || res.status === 403) return {
    kind: 'unauthorized'
  };
  if (!res.ok) return {
    kind: 'error',
    reason: 'server'
  };
  try {
    const json = await res.json();
    const raw = json !== null && typeof json === 'object' && 'data' in json ? json.data : json;
    if (raw === null || typeof raw !== 'object') return {
      kind: 'error',
      reason: 'server'
    };
    const user = parseMeProfile(raw);
    return user === null ? {
      kind: 'error',
      reason: 'server'
    } : {
      kind: 'user',
      user
    };
  } catch {
    return {
      kind: 'error',
      reason: 'server'
    };
  }
}
/** Shape a raw `/v1/me` payload object into a `MorfiUser`. */
function parseMeProfile(p) {
  if (typeof p.id !== 'string' || p.id.length === 0) return null;
  return {
    id: p.id,
    email: typeof p.email === 'string' ? p.email : '',
    phoneNumber: typeof p.phoneNumber === 'string' ? p.phoneNumber : null,
    displayName: typeof p.displayName === 'string' ? p.displayName : null,
    handle: typeof p.handle === 'string' ? p.handle : null,
    avatarUrl: typeof p.avatarUrl === 'string' ? p.avatarUrl : null,
    avatarNsfw: typeof p.avatarNsfw === 'boolean' ? p.avatarNsfw : null,
    avatarPreferences: p.avatarPreferences !== null && typeof p.avatarPreferences === 'object' && typeof p.avatarPreferences.rasterise === 'boolean' ? {
      rasterise: p.avatarPreferences.rasterise
    } : null,
    heroUrl: typeof p.heroUrl === 'string' ? p.heroUrl : null,
    coverNsfw: typeof p.heroNsfw === 'boolean' ? p.heroNsfw : null,
    bio: typeof p.bio === 'string' ? p.bio : null,
    country: typeof p.country === 'string' ? p.country : null,
    locationSource: typeof p.locationSource === 'string' ? p.locationSource : null,
    createdAt: typeof p.createdAt === 'string' ? p.createdAt : new Date().toISOString(),
    onboardingCompleted: p.onboardingCompleted === true,
    connectedProviders: Array.isArray(p.connectedProviders) ? p.connectedProviders.filter(provider => provider !== null && typeof provider === 'object').map(provider => ({
      provider: typeof provider.provider === 'string' ? provider.provider : '',
      ...(typeof provider.username === 'string' ? {
        username: provider.username
      } : {}),
      ...(typeof provider.displayName === 'string' ? {
        displayName: provider.displayName
      } : {})
    })) : [],
    turnkeySubOrgId: typeof p.turnkeySubOrgId === 'string' ? p.turnkeySubOrgId : null
  };
}
// ── User-scoped React Query key prefixes ──────────────────────────────────────
/**
 * React Query key prefixes that hold USER-SCOPED data and must be cleared
 * when a user logs out (or switches users). Anything not in this set is
 * treated as PUBLIC and stays in the cache.
 */
const USER_SCOPED_RQ_PREFIXES = new Set(['wallets',
// WALLETS_QUERY_KEY + ARCHIVED_WALLETS_QUERY_KEY (Turnkey wallets)
'auth-providers',
// AUTH_PROVIDERS_QUERY_KEY (linked OAuth providers)
'wallet-balance',
// WALLET_BALANCE_QUERY_KEY (per-wallet USDC/SOL)
'outcome-token-balance',
// OUTCOME_TOKEN_BALANCE_QUERY_KEY
'trade-balances',
// TRADE_BALANCES_QUERY_KEY
'claim-receipt',
// CLAIM_RECEIPT_QUERY_KEY (per-user claim status)
'merkle-user-share',
// MERKLE_USER_SHARE_QUERY_KEY (per-user merkle proof)
'user-activity',
// per-user activity feed
'me',
// private /v1/me projections
'auth' // session/auth projections
]);
function isUserScopedQuery(key) {
  const first = key[0];
  return key[1] === 'viewer' || typeof first === 'string' && USER_SCOPED_RQ_PREFIXES.has(first);
}
function clearUserScopedQueries(queryClient) {
  queryClient.removeQueries({
    predicate: q => isUserScopedQuery(q.queryKey)
  });
}
const DEFAULT_AUTH_STATE = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
  error: null
};
export const SdkAuthContext = createContext(null);
/** Hook for reading SdkAuthProvider context. */
export function useSdkAuthContext() {
  return useContext(SdkAuthContext);
}
function clearRejectedSessionHints() {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('morfi_user_id');
      localStorage.removeItem('morfi_bearer_token');
    }
  } catch {
    // SILENT-CATCH: localStorage may be blocked.
  }
}
async function tryRestoreSession(refreshSessionAsync) {
  if (!hasSessionHint()) return {
    kind: 'anonymous',
    reason: 'no-session'
  };
  // 1. Probe `/v1/me` with the existing cookie — no rotation.
  const probe = await probeMeProfile();
  if (probe.kind === 'user') return probe;
  // A transient failure (network / 5xx) is not an auth failure — do not
  // burn a refresh rotation chasing it; treat as "could not restore now".
  if (probe.kind === 'error') return {
    kind: 'deferred',
    reason: probe.reason
  };
  // 2. `/v1/me` was 401/403 — the cookie is missing, expired, or revoked.
  //    Attempt a single refresh; if it succeeds the cookie is rotated to a
  //    fresh token and `/v1/me` will then succeed.
  try {
    await refreshSessionAsync();
  } catch (error) {
    const reason = classifySessionFailure(error);
    if (reason !== 'auth-rejected') return {
      kind: 'deferred',
      reason
    };
    clearRejectedSessionHints();
    return {
      kind: 'anonymous',
      reason
    };
  }
  const refreshedProbe = await probeMeProfile();
  if (refreshedProbe.kind === 'user') return refreshedProbe;
  if (refreshedProbe.kind === 'error') {
    return {
      kind: 'deferred',
      reason: refreshedProbe.reason
    };
  }
  clearRejectedSessionHints();
  return {
    kind: 'anonymous',
    reason: 'auth-rejected'
  };
}
// ── Provider ──────────────────────────────────────────────────────────────────
/**
 * SDK-owned auth provider for web. Handles session-restore, logout, and the
 * auth-store mirror. Mount inside `<SDKProvider>` + `<AuthSyncProvider>` trees.
 *
 * App-specific side-effects (toast, analytics, onboarding reset, NSFW reset)
 * are injected via the `callbacks` prop — the SDK core has no dependency on
 * frontend-specific libraries.
 */
export function SdkAuthProvider({
  children,
  callbacks
}) {
  const [state, setState] = useState(DEFAULT_AUTH_STATE);
  const {
    turnkeyLogout,
    registerLogout,
    consumePendingConnect
  } = useAuthSync();
  const queryClient = useQueryClient();
  const viewerScope = useSdkViewerScope();
  const networkScope = useSdkNetworkScope();
  const logoutMut = useLogout();
  const refreshMut = useRefreshSession({
    meta: {
      skipGlobalErrorHandler: true
    }
  });
  // Keep callbacks in a ref so effects always call the latest version
  // without needing them as deps (they change every render). The ref is
  // synced in an effect — never written during render (a React-correctness
  // violation the React Compiler rejects).
  const callbacksRef = useRef(callbacks);
  const restoreInFlightRef = useRef(null);
  const logoutInFlightRef = useRef(null);
  useEffect(() => {
    callbacksRef.current = callbacks;
  });
  function emitSessionDiagnostic(signal) {
    callbacksRef.current?.onSessionDiagnostic?.({
      boundary: 'morfi-session',
      online: typeof navigator === 'undefined' ? true : navigator.onLine,
      platform: 'web',
      ...signal
    });
  }
  async function restoreSessionState(trigger, notifyRestored) {
    let pending = restoreInFlightRef.current;
    if (pending === null) {
      pending = tryRestoreSession(async () => {
        const refreshed = await refreshMut.mutateAsync({
          mode: 'extend'
        });
        // Hand the deadline to the scheduler so its bootstrap does not spend a
        // second round-trip asking for what this response already returned.
        recordSessionExpiry(refreshed.expiresAt);
        return refreshed;
      });
      restoreInFlightRef.current = pending;
    }
    let result;
    try {
      result = await pending;
    } catch {
      emitSessionDiagnostic({
        reason: 'server',
        trigger
      });
      callbacksRef.current?.onSessionRestoreError?.('restore_unexpected');
      setState(previous => ({
        ...previous,
        isLoading: false,
        error: null
      }));
      return;
    } finally {
      if (restoreInFlightRef.current === pending) restoreInFlightRef.current = null;
    }
    if (result.kind === 'deferred') {
      emitSessionDiagnostic({
        reason: result.reason,
        trigger
      });
      setState(previous_0 => ({
        ...previous_0,
        isLoading: false,
        error: null
      }));
      return;
    }
    if (result.kind === 'anonymous') {
      if (result.reason === 'auth-rejected') {
        emitSessionDiagnostic({
          reason: result.reason,
          trigger
        });
        clearUserScopedQueries(queryClient);
      }
      setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: null
      });
      return;
    }
    setState({
      isAuthenticated: true,
      isLoading: false,
      user: result.user,
      error: null
    });
    try {
      localStorage.setItem('morfi_user_id', result.user.id);
    } catch {
      // SILENT-CATCH: localStorage may be blocked.
    }
    if (notifyRestored) {
      try {
        callbacksRef.current?.onSessionRestored?.(result.user.id);
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        callbacksRef.current?.onSessionRestoreError?.(message);
      }
    }
  }
  const restoreSessionStateEvent = useEffectEvent(restoreSessionState);
  function setUser(user) {
    setState({
      isAuthenticated: user !== null,
      isLoading: false,
      user,
      error: null
    });
  }
  async function refreshSession() {
    await restoreSessionState('manual-refresh', false);
  }
  async function logout(source = 'user') {
    if (logoutInFlightRef.current !== null) return logoutInFlightRef.current;
    if (!state.isAuthenticated && !state.isLoading) return;
    const pending_0 = performLogout(source);
    logoutInFlightRef.current = pending_0;
    try {
      await pending_0;
    } finally {
      if (logoutInFlightRef.current === pending_0) logoutInFlightRef.current = null;
    }
  }
  async function performLogout(source_0) {
    const userId = state.user?.id ?? 'unknown';
    // Pre-logout hook: e.g. tear down engineer impersonation BEFORE the rest
    // of the signout sequence. The caller (frontend) provides `onPreLogout`
    // to call `teardownImpersonation('signout')` against the Zustand store.
    // Idempotent — no-op when no impersonation is active.
    if (callbacksRef.current?.onPreLogout) {
      await callbacksRef.current.onPreLogout();
    }
    callbacksRef.current?.onLogoutStart?.(source_0);
    // `allSettled` so a failing server logout does not skip the kit teardown,
    // and vice-versa. Mirrors the React Native provider.
    const results = await Promise.allSettled([logoutMut.mutateAsync({}),
    // v2 SDK — clear Morfi session (cookie + backend revocation)
    turnkeyLogout() // Clear Turnkey IndexedDB session
    ]);
    const failure = results.find(result_0 => result_0.status === 'rejected');
    let failureMessage = null;
    if (failure?.status === 'rejected') {
      const reason = failure.reason;
      failureMessage = reason instanceof Error ? reason.message : String(reason);
    }
    // A revoked session is the end-state logout wants, so it is reported as a
    // completed logout rather than a failure.
    const isSessionExpired = failureMessage !== null && (failureMessage.includes('expired') || failureMessage.includes('Session'));
    callbacksRef.current?.onResetIdentity?.();
    // A pending provider-connect captured before logout (redirect return, or
    // a stash left by a cancelled OAuth connect) must never attach to
    // whoever signs in next in this tab, and must not hijack a subsequent
    // OAuth login return through the connect diverters.
    consumePendingConnect();
    OauthConnectStash.clear();
    clearUserStorage();
    callbacksRef.current?.onClearNsfwRevealStore?.();
    // Targeted cache clear — only user-scoped data, not the whole cache.
    clearUserScopedQueries(queryClient);
    callbacksRef.current?.onResetOnboardingStore?.();
    // Exactly one terminal callback runs on every path. Both dismiss the
    // logout toast, so no outcome can leave it spinning forever.
    if (failureMessage === null || isSessionExpired) {
      callbacksRef.current?.onLogoutComplete?.(userId);
    } else {
      callbacksRef.current?.onLogoutError?.(failureMessage);
    }
    setState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      error: null
    });
  }
  // Register logout for session expiry handling. Intentionally re-registers
  // every render so the ref always points to the latest logout closure.
  useEffect(() => {
    registerLogout(logout);
  });
  // Defense-in-depth: watch the active user id and clear user-scoped caches
  // if it changes mid-session (e.g. an OAuth flow or session-restore landed
  // us as a different user without going through logout).
  const prevUserIdRef = useRef(null);
  useEffect(() => {
    const currentId = state.user?.id ?? null;
    const prevId = prevUserIdRef.current;
    if (prevId !== null && currentId !== prevId) {
      clearUserScopedQueries(queryClient);
      callbacksRef.current?.onResetOnboardingStore?.();
    }
    prevUserIdRef.current = currentId;
  }, [state.user?.id, queryClient]);
  // Initial session restore on mount.
  useEffect(() => {
    async function initialRefresh() {
      await restoreSessionStateEvent('initial-restore', true);
    }
    void initialRefresh();
  }, []);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onOnline = () => {
      void restoreSessionStateEvent('online', true);
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, []);
  // Proactive session refresh. Restore alone only refreshes AFTER `/v1/me`
  // rejects the cookie, which is too late to save the session — so without
  // this the browser session dies at its original expiry no matter how
  // actively the user browses. Gated on an authed user: an anonymous visitor
  // has no session to extend and would just 401 on every page load.
  const refreshForSchedulerEvent = useEffectEvent(async () => {
    const result_1 = await refreshMut.mutateAsync({
      mode: 'extend'
    });
    return {
      expiresAt: result_1.expiresAt
    };
  });
  const emitSchedulerDiagnosticEvent = useEffectEvent(({
    trigger: trigger_0,
    outcome
  }) => {
    callbacksRef.current?.onSessionRefresh?.({
      trigger: trigger_0,
      outcome
    });
    if (outcome === 'refreshed') return;
    emitSessionDiagnostic({
      reason: outcome === 'rejected' ? 'auth-rejected' : 'server',
      trigger: trigger_0
    });
  });
  const isAuthed = state.user !== null;
  useEffect(() => {
    if (!isAuthed || typeof document === 'undefined') return;
    return startRefreshScheduler({
      refreshAsync: () => refreshForSchedulerEvent(),
      onSessionRejected: () => {
        void restoreSessionStateEvent('scheduled-refresh-rejected', false);
      },
      onDiagnostic: emitSchedulerDiagnosticEvent
    });
  }, [isAuthed]);
  // Mirror auth state into the SDK-owned store so `useMorfiAuth()` — and
  // React Native — can read identity without the frontend AuthProvider.
  useEffect(() => {
    getSdkAuthStore().set(state.isLoading ? {
      status: 'loading',
      user: null
    } : state.user !== null ? {
      status: 'authed',
      user: state.user
    } : {
      status: 'anon',
      user: null
    });
  }, [state]);
  useEffect(() => {
    if (state.user !== null) {
      writeAuthPrivateAccount(queryClient, state.user, viewerScope, networkScope);
    }
  }, [networkScope, queryClient, state.user, viewerScope]);
  useEffect(() => {
    const userId_0 = state.user?.id;
    if (userId_0 === undefined) return;
    const sync = () => {
      setState(previous_1 => {
        if (previous_1.user === null || previous_1.user.id !== userId_0) return previous_1;
        const user_0 = readAuthPrivateAccount(queryClient, previous_1.user, viewerScope, networkScope);
        return user_0 === previous_1.user ? previous_1 : {
          ...previous_1,
          user: user_0
        };
      });
    };
    sync();
    return subscribeAuthPrivateAccount(queryClient, userId_0, viewerScope, networkScope, sync);
  }, [networkScope, queryClient, state.user?.id, viewerScope]);
  const value = {
    ...state,
    logout,
    refreshSession,
    setUser
  };
  return _jsx(SdkAuthContext.Provider, {
    value: value,
    children: _jsx(AuthActionsProvider, {
      value: {
        logout,
        refreshSession,
        setUser
      },
      children: children
    })
  });
}